import React, { useCallback, useEffect, useState } from 'react';
import RingLoader from 'react-spinners/RingLoader';
import { getContentLibrary, publishLibraryItem } from '../../services/aiComposer';
import { getUserAccessToken } from '../../services/userSession';
import './LibraryPickerModal.css';

function displaySlotNumber(slotPosition) {
  return slotPosition - 6;
}

export default function LibraryPickerModal({
  open,
  token,
  ownerUserId,
  screenId,
  slotId,
  onClose,
  onPublished,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [publishingId, setPublishingId] = useState(null);
  const [error, setError] = useState('');

  const authToken = token || getUserAccessToken();

  const load = useCallback(async () => {
    if (!open || !authToken || !ownerUserId) {
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await getContentLibrary(authToken, ownerUserId);
      setItems(response.data?.items || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load library.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [authToken, open, ownerUserId]);

  useEffect(() => {
    if (open) {
      load();
    }
  }, [load, open]);

  const handleSelect = async (item) => {
    if (!authToken || !ownerUserId || publishingId != null) {
      return;
    }

    setPublishingId(item.ItemId);
    setError('');
    try {
      const response = await publishLibraryItem(authToken, item.ItemId, {
        userId: ownerUserId,
        screenIds: [screenId],
        slotId,
      });

      if (response.data?.reviewPending) {
        window.alert('Your content was sent for approval before it goes live on this screen.');
      }

      onPublished?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not add library content to this slot.');
    } finally {
      setPublishingId(null);
    }
  };

  if (!open) {
    return null;
  }

  return (
    <div className="libraryPickerOverlay" onClick={onClose} role="presentation">
      <div className="libraryPickerModal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <h2 className="libraryPickerTitle">Choose from library</h2>
        <p className="libraryPickerSubtitle">Slot {displaySlotNumber(slotId)}</p>

        {loading ? (
          <div className="libraryPickerLoader">
            <RingLoader color="#dc5024" size={36} />
          </div>
        ) : null}

        {error ? <p className="libraryPickerError">{error}</p> : null}

        {!loading && items.length === 0 ? (
          <p className="libraryPickerEmpty">No library content yet. Create something with AI first.</p>
        ) : null}

        <div className="libraryPickerGrid">
          {items.map((item) => (
            <button
              key={item.ItemId}
              type="button"
              className="libraryPickerCard"
              onClick={() => handleSelect(item)}
              disabled={publishingId != null}
            >
              <div className="libraryPickerCardMedia">
                {item.MediaUrl?.endsWith('.mp4') ? (
                  <video src={item.MediaUrl} muted playsInline />
                ) : (
                  <img src={item.MediaUrl} alt={item.Title || 'Content'} />
                )}
              </div>
              <span className="libraryPickerCardTitle">{item.Title || 'Untitled'}</span>
              {publishingId === item.ItemId ? <span className="libraryPickerPublishing">Adding…</span> : null}
            </button>
          ))}
        </div>

        <button type="button" className="libraryPickerCancel" onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}
